import {ActiveElement, Chart, ChartEvent} from 'chart.js';
import {getRelativePosition} from 'chart.js/helpers';
import {Context} from 'chartjs-plugin-datalabels/types';
import {useCallback, useMemo, useRef, useState} from 'react';
import {Bar} from 'react-chartjs-2';

import {CounterBreakdown} from '@shared/models/api_models';

import {Custom} from '@shared_web/lib/react';
import {isTouchScreen} from '@shared_web/lib/touch_screen';
import {useGlobalMouseClick, useGlobalMouseMove} from '@shared_web/lib/use_global_mouse_event';

import {translate} from '@mavismart_frontend/components/translate';
import {MAIN_COLOR, TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';

interface BarChartProps {
  data: {ts: number; value: number}[];
  breakdown?: CounterBreakdown;
  onBarClick?: (index: number, data: {ts: number; value: number}) => void;
}

const BAR_COLOR = `${TEXT_MAIN_COLOR}`;
const BAR_COLOR_ACTIVE = MAIN_COLOR;

const PADDING_RIGHT_FOR_TOOLTIP = 32;
const PADDING_TOP_FOR_TOOLTIP = 16;

const IS_TOUCH_SCREEN = isTouchScreen();

export const CHART_HEIGHT = 200;

export const BarChart: Custom<BarChartProps, 'div'> = props => {
  const {data, breakdown = 'hour', onBarClick, ...rest} = props;

  const chartRef = useRef<Chart<'bar'>>();
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const account = useAccount();
  const locale = getUserLocale(account);
  const lang = getUserLanguage(account);

  const axisStrokeColorLight = '#f0f0f0';
  const axisLabelColor = '#aaaaaa';

  //
  // Render the label for the x-axis
  //
  const renderXAxisLabel = useCallback(
    (value: string | number): string => {
      const index = typeof value === 'number' ? value : parseFloat(value);
      const timestamp = data[index]?.ts;
      if (!timestamp) return '';

      const date = new Date(timestamp);

      if (breakdown === 'hour') {
        if (data.length > 24) {
          // For large datasets, show date and hour
          return date
            .toLocaleString(locale, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              timeZone: 'UTC',
            })
            .replace(/\./, '');
        }
        return date.getUTCHours().toString();
      }
      if (breakdown === 'day') {
        return date
          .toLocaleString(locale, {month: 'short', day: 'numeric', timeZone: 'UTC'})
          .replace(/\.$/, '');
      }

      return '';
    },
    [data, breakdown, locale]
  );

  //
  // Render the label for the y-axis
  //
  const renderYAxisLabel = useCallback(
    (value: string | number) => {
      const numValue = typeof value === 'number' ? value : parseFloat(value);

      // Only show integer values
      if (numValue % 1 !== 0) return '';

      // Format with k notation for values >= 1000
      if (numValue >= 1000) {
        const thousands = Math.floor(numValue / 1000);
        const hundreds = Math.round((numValue - thousands * 1000) / 100);

        if (hundreds === 0 || thousands >= 10) {
          return `${thousands.toLocaleString(locale)}k`;
        }
        // Format remainder with commas for readability
        return `${thousands}k${hundreds}`;
      }

      return numValue.toString();
    },
    [locale]
  );

  //
  // Render the label for the tooltip
  //
  const renderTooltipLabel = useCallback(
    (dataIndex: number, ctx: Context) => {
      const timestamp = data[ctx.dataIndex]?.ts;
      if (!timestamp) {
        return dataIndex.toLocaleString(locale);
      }

      const date = new Date(timestamp);
      let timeLabel = '';

      if (breakdown === 'hour') {
        timeLabel = `${date
          .toLocaleString(locale, {month: 'short', day: 'numeric', timeZone: 'UTC'})
          .replace(/\.$/, '')} • ${date.toLocaleString(locale, {
          hour: 'numeric',
          timeZone: 'UTC',
        })} ${translate('hour_to_hour', lang)} ${new Date(date.getUTCHours() + 1).toLocaleString(
          locale,
          {
            hour: 'numeric',
            timeZone: 'UTC',
          }
        )}`;
      } else if (breakdown === 'day') {
        timeLabel = date
          .toLocaleString(locale, {month: 'short', day: 'numeric', timeZone: 'UTC'})
          .replace(/\.$/, '')
          .replace(/^./, c => c.toLocaleUpperCase(locale));
      }

      return `${timeLabel} • ${dataIndex.toLocaleString(locale)}`;
    },
    [data, locale, breakdown, lang]
  );

  //
  // Chart events handler
  //

  const handleInteraction = useCallback((_event: ChartEvent, elements: ActiveElement[]) => {
    setSelectedIndex(elements[0]?.index);
    chartRef.current?.update();
  }, []);

  const handleClick = useCallback(
    (_event: ChartEvent, elements: ActiveElement[]) => {
      const index = elements[0]?.index;
      if (index === undefined) {
        return;
      }
      const dataPoint = data[index];
      if (dataPoint === undefined) {
        return;
      }

      // On touch screens: first tap shows tooltip, second tap zooms
      if (IS_TOUCH_SCREEN) {
        if (selectedIndex === index) {
          // Same bar tapped again - trigger zoom
          onBarClick?.(index, dataPoint);
        } else {
          // First tap on this bar - show tooltip
          handleInteraction(_event, elements);
        }
      } else {
        // On desktop: click directly zooms
        onBarClick?.(index, dataPoint);
      }
    },
    [data, onBarClick, selectedIndex, handleInteraction]
  );

  const handleHover = useCallback(
    (_event: ChartEvent, elements: ActiveElement[]) => {
      if (!IS_TOUCH_SCREEN) {
        handleInteraction(_event, elements);
      }
    },
    [handleInteraction]
  );

  //
  // Global mouse move or click handler
  //

  const isInsideChartArea = useCallback((event: Parameters<typeof getRelativePosition>[0]) => {
    if (!chartRef.current) {
      return false;
    }
    const {chartArea} = chartRef.current;
    const {left, right, top, bottom} = chartArea;
    const {x, y} = getRelativePosition(event, chartRef.current);
    return x >= left && x <= right && y >= top && y <= bottom;
  }, []);

  const handleGlobalMouseClick = useCallback(
    (event: MouseEvent) => {
      if (!isInsideChartArea(event) && selectedIndex !== undefined) {
        setSelectedIndex(undefined);
        chartRef.current?.update();
      }
      return true;
    },
    [selectedIndex, isInsideChartArea]
  );
  const handleGlobalMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!IS_TOUCH_SCREEN) {
        handleGlobalMouseClick(event);
      }
    },
    [handleGlobalMouseClick]
  );
  useGlobalMouseMove(handleGlobalMouseMove);
  useGlobalMouseClick(handleGlobalMouseClick);

  //
  // Render the chart
  //

  const chartData = useMemo(() => data.map(c => c.value), [data]);
  const chartLabels = useMemo(() => data.map((_, index) => index), [data]);

  return (
    <div {...rest}>
      <Bar
        ref={chartRef}
        height={CHART_HEIGHT}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          onClick: handleClick,
          onHover: handleHover,
          layout: {
            autoPadding: false,
            padding: {
              top: PADDING_TOP_FOR_TOOLTIP,
              right: PADDING_RIGHT_FOR_TOOLTIP,
            },
          },

          interaction: {intersect: false, mode: 'nearest', axis: 'x', includeInvisible: false},
          scales: {
            x: {
              ticks: {callback: renderXAxisLabel, color: axisLabelColor},
              grid: {display: false},
              border: {display: false},
            },
            y: {
              offset: false,
              type: 'linear',
              grid: {color: axisStrokeColorLight, tickColor: axisStrokeColorLight, tickLength: 3},
              ticks: {color: axisLabelColor, maxTicksLimit: 6, callback: renderYAxisLabel},
              border: {display: false},
              min: 0,
              suggestedMax: Math.max(...data.map(d => d.value)) * 1.1,
            },
          },

          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
            },
            datalabels: {
              labels: {
                value: {
                  display: (ctx: Context) => ctx.dataIndex === selectedIndex,
                  formatter: renderTooltipLabel,
                  align: 'top',
                  anchor: 'end',
                  offset: 4,
                  backgroundColor: BAR_COLOR_ACTIVE,
                  borderWidth: 0,
                  padding: 6,
                  color: '#ffffff',
                  font: {weight: 'bold'},
                },
              },
            },
          },
        }}
        data={{
          labels: chartLabels,
          datasets: [
            {
              data: chartData,
              minBarLength: 2,
              backgroundColor: (ctx: {dataIndex: number}) =>
                ctx.dataIndex === selectedIndex ? BAR_COLOR_ACTIVE : BAR_COLOR,
            },
          ],
        }}
      />
    </div>
  );
};

BarChart.displayName = 'BarChart';
